import * as React from "react"
import { Head, router } from "@inertiajs/react"
import { Plus } from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { Button } from "@/components/ui/button"
import { RecordActionsMenu } from "@/components/RecordActionsMenu"
import { TagBadge } from "@/components/tags/TagBadge"
import { TagForm, type TagRecord } from "@/components/tags/TagForm"
import { PipelineSubNav } from "../PipelineSubNav"
import type { PageProps } from "@/types/inertia"

type TagsProps = PageProps<{
  tags: TagRecord[]
}>

const DESCRIPTION =
  "Colorful labels you can attach to pieces. A piece can wear many tags — use them to relate cross-posts and slice your pipeline however you like."

export default function TagsIndex({ tags }: TagsProps) {
  const [formOpen, setFormOpen] = React.useState(false)
  const [editing, setEditing] = React.useState<TagRecord | null>(null)

  const openNew = () => {
    setEditing(null)
    setFormOpen(true)
  }
  const openEdit = (tag: TagRecord) => {
    setEditing(tag)
    setFormOpen(true)
  }
  const onDelete = (tag: TagRecord) => {
    if (!window.confirm(`Delete “${tag.name}”? It will be removed from every piece that has it.`)) return
    router.delete(`/tags/${tag.id}`, { preserveScroll: true })
  }

  return (
    <>
      <Head title="Tags">
        <meta name="description" content={DESCRIPTION} />
        <meta property="og:title" content="Tags" />
        <meta property="og:description" content={DESCRIPTION} />
      </Head>
      <AppShell wide>
        <div className="flex items-end justify-between border-b border-hairline pb-6">
          <h1>Pipeline</h1>
          <Button type="button" onClick={openNew}>
            <Plus className="h-4 w-4" />
            New tag
          </Button>
        </div>

        <PipelineSubNav active="tags" />

        <section className="mt-8 flex max-w-2xl flex-col gap-4">
          <div>
            <h2>Tags</h2>
            <p className="mt-1 text-ink-muted">{DESCRIPTION}</p>
          </div>

          {tags.length === 0 ? (
            <p className="text-ink-muted">No tags yet.</p>
          ) : (
            <ul className="divide-y divide-hairline overflow-hidden rounded-md border border-hairline bg-page">
              {tags.map((tag) => (
                <li key={tag.id} className="flex items-center justify-between gap-3 px-4 py-3">
                  <span className="min-w-0 flex-1">
                    <TagBadge tag={tag} />
                  </span>
                  <RecordActionsMenu
                    label={tag.name}
                    onEdit={() => openEdit(tag)}
                    onDelete={() => onDelete(tag)}
                  />
                </li>
              ))}
            </ul>
          )}
        </section>

        <TagForm open={formOpen} onOpenChange={setFormOpen} tag={editing} />
      </AppShell>
    </>
  )
}
